import {
  AlertCircle,
  CheckCircle2,
  Info,
  TriangleAlert,
  X,
} from 'lucide-react';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

import { useLanguage } from '../../contexts/LanguageContext';

const AlertContext = createContext(null);

const alertIcons = {
  success: CheckCircle2,
  error: AlertCircle,
  warning: TriangleAlert,
  info: Info,
};

function AlertProvider({ children }) {
  const { t, language } = useLanguage();
  const [alerts, setAlerts] = useState([]);

  const direction = String(language || '')
    .toLowerCase()
    .startsWith('ar')
    ? 'rtl'
    : 'ltr';

  const removeAlert = useCallback((id) => {
    setAlerts((currentAlerts) =>
      currentAlerts.filter(
        (alert) => alert.id !== id,
      ),
    );
  }, []);

  const showAlert = useCallback(
    ({
      type = 'info',
      title,
      message,
      duration = 4500,
    }) => {
      const id = `${Date.now()}-${Math.random()}`;

      const newAlert = {
        id,
        type,
        title:
          title ||
          t(`alerts.${type}`),
        message,
      };

      setAlerts((currentAlerts) => [
        newAlert,
        ...currentAlerts,
      ]);

      if (duration > 0) {
        window.setTimeout(() => {
          removeAlert(id);
        }, duration);
      }

      return id;
    },
    [removeAlert, t],
  );

  const clearAlerts = useCallback(() => {
    setAlerts([]);
  }, []);

  const value = useMemo(
    () => ({
      alerts,
      showAlert,
      removeAlert,
      clearAlerts,
    }),
    [
      alerts,
      showAlert,
      removeAlert,
      clearAlerts,
    ],
  );

  const alertsPortal = (
    <div
      className="app-alerts"
      dir={direction}
      aria-live="polite"
      aria-atomic="true"
    >
      {alerts.map((alert) => {
        const AlertIcon =
          alertIcons[alert.type] ||
          Info;

        return (
          <article
            className={`app-alert app-alert--${alert.type}`}
            key={alert.id}
          >
            <span className="app-alert__icon">
              <AlertIcon size={20} />
            </span>

            <div className="app-alert__content">
              <strong>
                {alert.title}
              </strong>

              {alert.message ? (
                <p>{alert.message}</p>
              ) : null}
            </div>

            <button
              type="button"
              className="app-alert__close"
              onClick={() =>
                removeAlert(alert.id)
              }
              aria-label={t(
                'alerts.close',
              )}
            >
              <X size={17} />
            </button>
          </article>
        );
      })}
    </div>
  );

  return (
    <AlertContext.Provider value={value}>
      {children}

      {typeof document !== 'undefined'
        ? createPortal(
            alertsPortal,
            document.body,
          )
        : null}
    </AlertContext.Provider>
  );
}

export const useAlerts = () => {
  const context =
    useContext(AlertContext);

  if (!context) {
    throw new Error(
      'useAlerts must be used inside AlertProvider',
    );
  }

  return context;
};

export default AlertProvider;
